/**
 * ioLiberum Outlook Add-in — Client-side Auth Service
 * Manages JWT tokens in localStorage.
 *
 * Auth flow on startup:
 *  1. If a valid non-expired token exists → use it, done.
 *  2. Otherwise, try silent login via Outlook identity (loginWithOfficeEmail).
 *     If the Outlook user's email is on the server's authorized list, a JWT is
 *     issued automatically — the user never sees the login screen.
 *  3. If silent auth fails (unauthorized email, network error) → show login form.
 */

const TOKEN_KEY = 'ioliberum_token';
const EMAIL_KEY = 'ioliberum_email';
const ADMIN_KEY = 'ioliberum_is_admin';

/** 30 min early-refresh window — refresh the token before it actually expires. */
const EARLY_REFRESH_SECONDS = 60 * 30;

/**
 * Parse a JWT payload without verifying the signature (client-side only).
 * @param {string} token
 * @returns {{ exp?: number, email?: string } | null}
 */
function parseJWT(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload;
    } catch {
        return null;
    }
}

/**
 * Check whether the stored token is still valid with at least EARLY_REFRESH_SECONDS remaining.
 * Does NOT make a network call.
 * @returns {boolean}
 */
export function isTokenFresh() {
    const token = getToken();
    if (!token) return false;
    const payload = parseJWT(token);
    if (!payload?.exp) return false;
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp - nowSeconds > EARLY_REFRESH_SECONDS;
}

/**
 * Login with email and password (manual login form).
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{token: string, email: string}>}
 */
export async function login(email, password) {
    const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Login failed (${response.status})`);
    }

    const data = await response.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(EMAIL_KEY, data.email);
    localStorage.setItem(ADMIN_KEY, String(data.is_admin || false));
    return data;
}

/**
 * Silent login using the Outlook user's email address.
 * The server validates the email against its AUTHORIZED_EMAILS list
 * and returns a JWT without requiring a password. The shared ADDIN_SECRET
 * header prevents abuse from arbitrary callers.
 *
 * @param {string} outlookEmail  From Office.context.mailbox.userProfile.emailAddress
 * @returns {Promise<boolean>}   true if authenticated, false otherwise
 */
export async function loginWithOfficeEmail(outlookEmail) {
    if (!outlookEmail) return false;

    try {
        const response = await fetch('/api/auth/login-office', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // The shared secret lives only on the server and is baked
                // into the Vite build via VITE_ADDIN_SECRET env var so it
                // is never hard-coded here.
                'X-Addin-Secret': import.meta.env.VITE_ADDIN_SECRET || '',
            },
            body: JSON.stringify({ email: outlookEmail }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (data.token) {
            localStorage.setItem(TOKEN_KEY, data.token);
            localStorage.setItem(EMAIL_KEY, data.email);
            localStorage.setItem(ADMIN_KEY, String(data.is_admin || false));
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Verify the stored token is still valid (server-side check).
 * Used as a fallback when the local expiry check is inconclusive.
 * @returns {Promise<boolean>}
 */
export async function verifyToken() {
    const token = getToken();
    if (!token) return false;

    try {
        const response = await fetch('/api/auth/verify', {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) return false;
        const data = await response.json();
        localStorage.setItem(ADMIN_KEY, String(data.is_admin || false));
        return true;
    } catch {
        return false;
    }
}

/**
 * Get the stored JWT token.
 * @returns {string|null}
 */
export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get the stored user email.
 * @returns {string|null}
 */
export function getEmail() {
    return localStorage.getItem(EMAIL_KEY);
}

/**
 * Check if a token exists (does NOT verify server-side).
 * @returns {boolean}
 */
export function hasToken() {
    return !!localStorage.getItem(TOKEN_KEY);
}

export function isAdmin() {
    return localStorage.getItem(ADMIN_KEY) === 'true';
}

/**
 * Logout — clear stored credentials.
 */
export function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(ADMIN_KEY);
}
